import { useContext, useState } from "react";
import { Context } from "../../../../context/Context";
import { useRestaurantAccess } from "../../../../access/hooks/useRestaurantAccess";
import { DeleteModal } from "../../../modals/Delete";
import { translations } from "../../../../utils/lang";
import "./DailyExpand.css";
import { DAILY_FIELDS2 } from "../../../../utils/Daily";
import {
  deleteDailyAPI,
  updateDailyAPI,
  updateDailyByStoreDateAppAPI,
  updateDisapproveByStoreDateAppAPI,
  updateApprovalByStoreDateAPI,
} from "../../../../utils/api/report";
import { formatter, formatter2 } from "../../../../utils/helpers";
import { Button, Repeat, ThumbsDown, ThumbsUp, Trash2 } from "../../../ui";
import {
  getMonthKey,
  groupBy,
  groupTotals,
  parseReportDate,
  pendingMarker,
  rowDayKey,
  rowMonthKey,
  rowToggleProps,
  sortMonthEntries,
  stopRowToggle,
  storeIdsOf,
} from "../../reportUtils";
import { NodeState } from "../../reportNode";
import { getFieldLabel, getReportText } from "../../reportText";
import { MONTHLY_FIELDS as AR_FIELD_LABELS } from "../../../../utils/Monthly";

const SUM_KEYS = ["completed", "completedAmount", "rejected", "canceled", "endurance", "refund", "rejectedAmount", "canceledAmount", "enduranceAmount", "refundAmount"];

// `groups` (optional): server-side totals per month / day / app for the store's whole filtered set. The headers print those instead of summing
// the rows they were given. Without `groups` (older backend) the headers sum `data`.
// `paged` (optional, the server-paged table): `data` then only carries the hierarchy levels (usePagedReport), the branch rows of the open day arrive in
// `paged.leafRows`, and opening a level reports the new path through `paged.onNode`.
export const DailyExpand = ({ data = [], onRefresh, groups = null, paged = null }) => {
  const {
    lang,
    handleTokenExpiration,
  } = useContext(Context);
  const { has, canForAll } = useRestaurantAccess();
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [expandedDate, setExpandedDate] = useState(null);
  const [expandedApp, setExpandedApp] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editItem, setEditItem] = useState({});
  // One mutation at a time; failures are shown instead of swallowed.
  const [pending, setPending] = useState(false);
  const [actionError, setActionError] = useState("");
  const text = getReportText(lang);
  const maps = {
    app: translations[lang].filter.apps,
  };

  // Group by "MonthName-YYYY" (explicitly parsed; see reportUtils).
  const groupedByMonth = groupBy(data, (item) =>
    getMonthKey(item[DAILY_FIELDS2.date]),
  );

  // Keyed by the record id: the same branch name appears under every date
  // and app, and keying by name let a save hit a different record.
  const handleEdit = (item) => {
    setIsEditing(item._id);
    setEditItem(item);
  };

  const handleCancel = () => {
    setIsEditing(null);
    setEditItem({});
  };

  const handleChange = (field, value) => {
    setEditItem((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const toggleMonth = (date, items) => {
    paged?.onNode(expandedMonth === date ? {} : { month: rowMonthKey(items[0], "daily") });
    setExpandedMonth((prev) => (prev === date ? null : date));
    setExpandedDate(null);
    setExpandedApp(null);
  };

  const toggleDate = (date, items) => {
    const month = rowMonthKey(items[0], "daily");
    paged?.onNode(expandedDate === date ? { month } : { month, date: rowDayKey(items[0]) });
    setExpandedDate((prev) => (prev === date ? null : date));
    setExpandedApp(null);
  };

  const toggleApp = (app) => {
    setExpandedApp((prev) => (prev === app ? null : app));
  };
  const confirmDelete = (id) => {
    setDeleteTarget(id);
  };

  // Runs a mutation; the API helpers resolve to undefined on failure.
  const runMutation = async (request, onSuccess) => {
    if (pending) return false;
    const token = localStorage.getItem("auth-token");
    if (!token) {
      handleTokenExpiration();
      return false;
    }
    setPending(true);
    setActionError("");
    try {
      const result = await request(token);
      if (!result) {
        setActionError(text.actionFailed);
        return false;
      }
      onSuccess?.();
      onRefresh?.(); // refresh parent
      return true;
    } catch (err) {
      console.error("Daily report update failed:", err);
      setActionError(text.actionFailed);
      return false;
    } finally {
      setPending(false);
    }
  };

  const handleConfirmedDeleteRaw = (id) =>
    runMutation(
      (token) => deleteDailyAPI(token, id),
      () => setDeleteTarget(null),
    );

  const approveDataRaw = (store, date, app, storeIds) =>
    runMutation((token) =>
      updateDailyByStoreDateAppAPI(token, store, date, app, storeIds),
    );

  const approveByMonthRaw = (store, date, approve, storeIds) =>
    runMutation((token) =>
      updateApprovalByStoreDateAPI(token, "daily", store, date, approve, storeIds),
    );

  const disapproveDataRaw = (store, date, app, storeIds) =>
    runMutation((token) =>
      updateDisapproveByStoreDateAppAPI(token, "daily", store, date, app, storeIds),
    );

  // Saves the record that is being edited (not whichever row is rendered).
  const handleSaveRaw = () =>
    runMutation(
      (token) => updateDailyAPI(token, editItem._id, editItem),
      () => {
        setIsEditing(null);
        setEditItem({});
      },
    );



  // Handler-level authorization: re-checked right before every request, against
  // the restaurant(s) the record belongs to (report rows carry the restaurant _id,
  // resolved to the public id by useRestaurantAccess). Hidden buttons are not enough.
  const refusedMutation = Promise.resolve(false);
  const handleConfirmedDelete = (id) =>
    canForAll("report.entry.delete", [[...data, ...(paged?.leafRows || [])].find((row) => row._id === id)?.storeId])
      ? handleConfirmedDeleteRaw(id)
      : refusedMutation;
  const handleSave = () =>
    canForAll("report.entry.update", [editItem?.storeId]) ? handleSaveRaw() : refusedMutation;
  const approveData = (store, date, app, storeIds) =>
    canForAll("report.approve", storeIds) ? approveDataRaw(store, date, app, storeIds) : refusedMutation;
  const disapproveData = (store, date, app, storeIds) =>
    canForAll("report.approve", storeIds) ? disapproveDataRaw(store, date, app, storeIds) : refusedMutation;
  const approveByMonth = (store, date, approve, storeIds) =>
    canForAll("report.approve", storeIds) ? approveByMonthRaw(store, date, approve, storeIds) : refusedMutation;


  return (
    <div className="report-daily-card-col">
      {actionError && (
        <p className="report-action-error" role="alert">
          {actionError}
        </p>
      )}
      {sortMonthEntries(groupedByMonth.entries(), paged?.ascending)
        .map(([month, monthItems]) => {
          // Day of month -> records (ascending, as numeric object keys were).
          const dates = monthItems.reduce((acc, item) => {
            const day = parseReportDate(item[DAILY_FIELDS2.date])?.day;
            if (!day) return acc;
            acc[day] = acc[day] || [];
            acc[day].push(item);
            return acc;
          }, {});

          const monthSummary = monthItems.reduce(
            (acc, item) => {
              acc.completed += parseFloat(item[DAILY_FIELDS2.completed] || 0);
              acc.completedAmount += parseFloat(
                item[DAILY_FIELDS2.completedAmount] || 0
              );
              acc.rejected += parseFloat(item[DAILY_FIELDS2.rejected] || 0);
              acc.canceled += parseFloat(item[DAILY_FIELDS2.canceled] || 0);
              acc.endurance += parseFloat(item[DAILY_FIELDS2.endurance] || 0);
              acc.refund += parseFloat(item[DAILY_FIELDS2.refund] || 0);
              acc.rejectedAmount += parseFloat(
                item[DAILY_FIELDS2.rejectedAmount] || 0
              );
              acc.canceledAmount += parseFloat(
                item[DAILY_FIELDS2.canceledAmount] || 0
              );
              acc.enduranceAmount += parseFloat(
                item[DAILY_FIELDS2.enduranceAmount] || 0
              );
              acc.refundAmount += parseFloat(
                item[DAILY_FIELDS2.refundAmount] || 0
              );
              return acc;
            },
            {
              completed: 0,
              completedAmount: 0,
              rejected: 0,
              canceled: 0,
              endurance: 0,
              refund: 0,
              rejectedAmount: 0,
              canceledAmount: 0,
              enduranceAmount: 0,
              refundAmount: 0,
            }
          );
          const monthTotals = groupTotals(groups, { month: rowMonthKey(monthItems[0], "daily") }, SUM_KEYS);
          if (monthTotals) Object.assign(monthSummary, monthTotals);
          const isMonthApproved = pendingMarker(monthTotals, monthItems.find((a) => a.isApproved === false));
          return (
            <div key={month}>
              <div
                className={`report-daily-summary-card month  ${
                  isMonthApproved && !isMonthApproved?.isApproved
                    ? "notApproved"
                    : ""
                }`}
                {...rowToggleProps(expandedMonth === month, () =>
                  toggleMonth(month, monthItems),
                )}
              >
                <div className="report-daily-p center">
                  <p>
                    <strong>
                      {translations[lang].filter.month[month.split("-")[0]] ??
                        month.split("-")[0]}
                      -{month.split("-")[1]}
                    </strong>
                  </p>
                  {isMonthApproved && (
                    <span className="report-pending-tag">{text.pendingApproval}</span>
                  )}
                </div>
                <div className="report-daily-p">
                  {` ${formatter2.format(
                    monthSummary.completed
                  )} / ${formatter.format(
                    monthSummary.completedAmount.toFixed(2)
                  )}`}
                </div>
                <div className="report-daily-p">
                  {` ${formatter2.format(
                    monthSummary.rejected
                  )} / ${formatter.format(monthSummary.rejectedAmount)}`}
                </div>
                <div className="report-daily-p">
                  {` ${formatter2.format(
                    monthSummary.canceled
                  )} / ${formatter.format(monthSummary.canceledAmount)}`}
                </div>
                <div className="report-daily-p">
                  {` ${formatter2.format(
                    monthSummary.endurance
                  )} / ${formatter.format(monthSummary.enduranceAmount)}`}
                </div>
                <div className="report-daily-p">
                  {` ${formatter2.format(
                    monthSummary.refund
                  )} / ${formatter.format(monthSummary.refundAmount)}`}
                </div>
                <div>
                  {has("report.approve") &&
                    (isMonthApproved && !isMonthApproved?.isApproved ? (
                      <Button
                        variant="danger"
                        appearance="soft"
                        rounded
                        icon={<ThumbsUp size={20} />}
                        aria-label={text.approveMonth}
                        title={text.approveMonth}
                        disabled={pending}
                        onClick={stopRowToggle(() =>
                          approveByMonth(monthItems[0].storeName, monthItems[0].date, true, storeIdsOf(monthItems)),
                        )}
                      >
                      </Button>
                    ) : (
                      <Button
                        variant="danger"
                        appearance="soft"
                        rounded
                        icon={<ThumbsDown size={20} />}
                        aria-label={text.disapproveMonth}
                        title={text.disapproveMonth}
                        disabled={pending}
                        onClick={stopRowToggle(() =>
                          approveByMonth(monthItems[0].storeName, monthItems[0].date, false, storeIdsOf(monthItems)),
                        )}
                      >
                      </Button>
                    ))}
                </div>
              </div>

              {expandedMonth === month && paged?.detailPending && <NodeState state={paged.detail} />}
              {expandedMonth === month && !paged?.detailPending && (
                <div className="expand-apps">
                  {(paged?.ascending ? Object.entries(dates) : Object.entries(dates).reverse()).map(([date, dateItems]) => {
                    const apps = dateItems.reduce((acc, item) => {
                      const app = item[DAILY_FIELDS2.app] || "Unknown App";
                      acc[app] = acc[app] || [];
                      acc[app].push(item);
                      return acc;
                    }, {});

                    const dateSummary = dateItems.reduce(
                      (acc, item) => {
                        acc.completed += parseFloat(
                          item[DAILY_FIELDS2.completed] || 0
                        );
                        acc.completedAmount += parseFloat(
                          item[DAILY_FIELDS2.completedAmount] || 0
                        );
                        acc.rejected += parseFloat(
                          item[DAILY_FIELDS2.rejected] || 0
                        );
                        acc.canceled += parseFloat(
                          item[DAILY_FIELDS2.canceled] || 0
                        );
                        acc.endurance += parseFloat(
                          item[DAILY_FIELDS2.endurance] || 0
                        );
                        acc.refund += parseFloat(
                          item[DAILY_FIELDS2.refund] || 0
                        );
                        acc.rejectedAmount += parseFloat(
                          item[DAILY_FIELDS2.rejectedAmount] || 0
                        );
                        acc.canceledAmount += parseFloat(
                          item[DAILY_FIELDS2.canceledAmount] || 0
                        );
                        acc.enduranceAmount += parseFloat(
                          item[DAILY_FIELDS2.enduranceAmount] || 0
                        );
                        acc.refundAmount += parseFloat(
                          item[DAILY_FIELDS2.refundAmount] || 0
                        );
                        return acc;
                      },
                      {
                        completed: 0,
                        completedAmount: 0,
                        rejected: 0,
                        canceled: 0,
                        endurance: 0,
                        refund: 0,
                        rejectedAmount: 0,
                        canceledAmount: 0,
                        enduranceAmount: 0,
                        refundAmount: 0,
                      }
                    );
                    const dayTotals = groupTotals(groups, { month: rowMonthKey(dateItems[0], "daily"), date: rowDayKey(dateItems[0]) }, SUM_KEYS);
                    if (dayTotals) Object.assign(dateSummary, dayTotals);
                    const isDayApproved = pendingMarker(dayTotals, dateItems.find((a) => a.isApproved === false));
                    return (
                      <div key={date} className="expand-app-section">
                        <div
                          className={`report-daily-summary-card date  ${
                            isDayApproved && !isDayApproved?.isApproved
                              ? "notApproved"
                              : ""
                          }`}
                          {...rowToggleProps(expandedDate === date, () =>
                            toggleDate(date, dateItems),
                          )}
                        >
                          <div className="report-daily-p center">
                            <p>
                              <strong>{date}</strong>
                            </p>
                          </div>
                          <div className="report-daily-p">
                            {` ${formatter2.format(
                              dateSummary.completed
                            )} / ${formatter.format(
                              dateSummary.completedAmount.toFixed(2)
                            )}`}
                          </div>
                          <div className="report-daily-p">
                            {` ${formatter2.format(
                              dateSummary.rejected
                            )} / ${formatter.format(
                              dateSummary.rejectedAmount
                            )}`}
                          </div>
                          <div className="report-daily-p">
                            {` ${formatter2.format(
                              dateSummary.canceled
                            )} / ${formatter.format(
                              dateSummary.canceledAmount
                            )}`}
                          </div>
                          <div className="report-daily-p">
                            {` ${formatter2.format(
                              dateSummary.endurance
                            )} / ${formatter.format(
                              dateSummary.enduranceAmount
                            )}`}
                          </div>
                          <div className="report-daily-p">
                            {` ${formatter2.format(
                              dateSummary.refund
                            )} / ${formatter.format(dateSummary.refundAmount)}`}
                          </div>
                        </div>

                        {expandedDate === date && (
                          <div className="expand-apps">
                            {Object.entries(apps).map(([app, appItems]) => {
                              const branches = (paged ? paged.leafRows.filter((row) => row.app === app) : appItems).reduce((acc, item) => {
                                const branch =
                                  item[DAILY_FIELDS2.branchName] ||
                                  "Unknown Branch";
                                acc[branch] = acc[branch] || [];
                                acc[branch].push(item);
                                return acc;
                              }, {});

                              const appSummary = appItems.reduce(
                                (acc, item) => {
                                  acc.completed += parseFloat(
                                    item[DAILY_FIELDS2.completed] || 0
                                  );
                                  acc.completedAmount += parseFloat(
                                    item[DAILY_FIELDS2.completedAmount] || 0
                                  );
                                  acc.rejected += parseFloat(
                                    item[DAILY_FIELDS2.rejected] || 0
                                  );
                                  acc.canceled += parseFloat(
                                    item[DAILY_FIELDS2.canceled] || 0
                                  );
                                  acc.endurance += parseFloat(
                                    item[DAILY_FIELDS2.endurance] || 0
                                  );
                                  acc.refund += parseFloat(
                                    item[DAILY_FIELDS2.refund] || 0
                                  );
                                  acc.rejectedAmount += parseFloat(
                                    item[DAILY_FIELDS2.rejectedAmount] || 0
                                  );
                                  acc.canceledAmount += parseFloat(
                                    item[DAILY_FIELDS2.canceledAmount] || 0
                                  );
                                  acc.enduranceAmount += parseFloat(
                                    item[DAILY_FIELDS2.enduranceAmount] || 0
                                  );
                                  acc.refundAmount += parseFloat(
                                    item[DAILY_FIELDS2.refundAmount] || 0
                                  );
                                  return acc;
                                },
                                {
                                  completed: 0,
                                  completedAmount: 0,
                                  rejected: 0,
                                  canceled: 0,
                                  endurance: 0,
                                  refund: 0,
                                  rejectedAmount: 0,
                                  canceledAmount: 0,
                                  enduranceAmount: 0,
                                  refundAmount: 0,
                                }
                              );

                              const appTotals = groupTotals(groups, { month: rowMonthKey(appItems[0], "daily"), date: rowDayKey(appItems[0]), app: appItems[0].app ?? null }, SUM_KEYS);
                              if (appTotals) Object.assign(appSummary, appTotals);
                              const isAppApproved = pendingMarker(appTotals, appItems.find((a) => a.isApproved === false));

                              return (
                                <div key={app} className="expand-app-section">
                                  <div
                                    className={`report-daily-summary-card app  ${
                                      isAppApproved &&
                                      !isAppApproved?.isApproved
                                        ? "notApproved"
                                        : ""
                                    }`}
                                    {...rowToggleProps(expandedApp === app, () =>
                                      toggleApp(app),
                                    )}
                                  >
                                    <div className="report-daily-p center">
                                      <p>
                                        <strong>
                                          {maps.app?.[app] ??
                                            translations[lang].notApp +
                                              " " +
                                              app[app.length - 1]}
                                        </strong>
                                      </p>
                                    </div>
                                    <div className="report-daily-p">
                                      {` ${formatter2.format(
                                        appSummary.completed
                                      )} / ${formatter.format(
                                        appSummary.completedAmount.toFixed(2)
                                      )}`}
                                    </div>
                                    <div className="report-daily-p">
                                      {` ${formatter2.format(
                                        appSummary.rejected
                                      )} / ${formatter.format(
                                        appSummary.rejectedAmount
                                      )}`}
                                    </div>
                                    <div className="report-daily-p">
                                      {` ${formatter2.format(
                                        appSummary.canceled
                                      )} / ${formatter.format(
                                        appSummary.canceledAmount
                                      )}`}
                                    </div>
                                    <div className="report-daily-p">
                                      {` ${formatter2.format(
                                        appSummary.endurance
                                      )} / ${formatter.format(
                                        appSummary.enduranceAmount
                                      )}`}
                                    </div>
                                    <div className="report-daily-p">
                                      {` ${formatter2.format(
                                        appSummary.refund
                                      )} / ${formatter.format(
                                        appSummary.refundAmount
                                      )}`}
                                    </div>
                                    <div>
                                      {has("report.approve") &&
                                        (isAppApproved &&
                                        !isAppApproved?.isApproved ? (
                                          <Button
                                            variant="danger"
                                            appearance="soft"
                                            rounded
                                            icon={<ThumbsUp size={20} />}
                                            aria-label={text.approveApp}
                                            title={text.approveApp}
                                            disabled={pending}
                                            onClick={stopRowToggle(() =>
                                              approveData(appItems[0].storeName, appItems[0].date, app, storeIdsOf(appItems)),
                                            )}
                                          >
                                          </Button>
                                        ) : (
                                          <Button
                                            variant="danger"
                                            appearance="soft"
                                            rounded
                                            icon={<ThumbsDown size={20} />}
                                            aria-label={text.disapproveApp}
                                            title={text.disapproveApp}
                                            disabled={pending}
                                            onClick={stopRowToggle(() =>
                                              disapproveData(appItems[0].storeName, appItems[0].date, app, storeIdsOf(appItems)),
                                            )}
                                          >
                                          </Button>
                                        ))}
                                    </div>
                                  </div>

                                  {expandedApp === app && (
                                    <div className="expand-branches">
                                      {paged && <NodeState state={paged.leaf} />}
                                      {Object.entries(branches).map(
                                        ([branch, branchItems]) => {
                                          // Calculate branch summary
                                          const branchSummary =
                                            branchItems.reduce(
                                              (acc, item) => {
                                                acc.completed += parseFloat(
                                                  item[
                                                    DAILY_FIELDS2.completed
                                                  ] || 0
                                                );
                                                acc.completedAmount +=
                                                  parseFloat(
                                                    item[
                                                      DAILY_FIELDS2
                                                        .completedAmount
                                                    ] || 0
                                                  );
                                                acc.rejected += parseFloat(
                                                  item[
                                                    DAILY_FIELDS2.rejected
                                                  ] || 0
                                                );
                                                acc.canceled += parseFloat(
                                                  item[
                                                    DAILY_FIELDS2.canceled
                                                  ] || 0
                                                );
                                                acc.endurance += parseFloat(
                                                  item[
                                                    DAILY_FIELDS2.endurance
                                                  ] || 0
                                                );
                                                acc.refund += parseFloat(
                                                  item[DAILY_FIELDS2.refund] ||
                                                    0
                                                );
                                                acc.rejectedAmount +=
                                                  parseFloat(
                                                    item[
                                                      DAILY_FIELDS2
                                                        .rejectedAmount
                                                    ] || 0
                                                  );
                                                acc.canceledAmount +=
                                                  parseFloat(
                                                    item[
                                                      DAILY_FIELDS2
                                                        .canceledAmount
                                                    ] || 0
                                                  );
                                                acc.enduranceAmount +=
                                                  parseFloat(
                                                    item[
                                                      DAILY_FIELDS2
                                                        .enduranceAmount
                                                    ] || 0
                                                  );
                                                acc.refundAmount += parseFloat(
                                                  item[
                                                    DAILY_FIELDS2.refundAmount
                                                  ] || 0
                                                );

                                                return acc;
                                              },
                                              {
                                                completed: 0,
                                                completedAmount: 0,
                                                rejected: 0,
                                                canceled: 0,
                                                endurance: 0,
                                                refund: 0,
                                                rejectedAmount: 0,
                                                canceledAmount: 0,
                                                enduranceAmount: 0,
                                                refundAmount: 0,
                                              }
                                            );

                                          // Previously checked monthItems, so every
                                          // branch showed "not approved" whenever any
                                          // record in the month was.
                                          const isBranchApproved =
                                            branchItems.find(
                                              (a) => a.isApproved === false
                                            );
                                          const editingThis =
                                            isEditing === branchItems[0]._id;

                                          return (
                                            <div
                                              key={branch}
                                              className={`report-daily-summary-card ${
                                                has("report.approve")
                                                  ? "branch"
                                                  : ""
                                              }  ${
                                                isBranchApproved &&
                                                !isBranchApproved?.isApproved
                                                  ? "notApproved"
                                                  : ""
                                              }`}
                                            >
                                              <div className="report-daily-p center branch">
                                                <p>
                                                  <strong>{branch}</strong>
                                                </p>
                                              </div>
                                              <div className="report-daily-p">
                                                {editingThis ? (
                                                  <>
                                                    <input
                                                      aria-label={`${branch} – ${getFieldLabel(lang, "completed", AR_FIELD_LABELS)}`}
                                                      className="daily-expand-input"
                                                      type="number"
                                                      value={
                                                        editItem[
                                                          DAILY_FIELDS2
                                                            .completed
                                                        ] || ""
                                                      }
                                                      onChange={(e) =>
                                                        handleChange(
                                                          DAILY_FIELDS2.completed,
                                                          e.target.value
                                                        )
                                                      }
                                                    />
                                                    <input
                                                      aria-label={`${branch} – ${getFieldLabel(lang, "completedAmount", AR_FIELD_LABELS)}`}
                                                      className="daily-expand-input"
                                                      type="number"
                                                      value={
                                                        editItem[
                                                          DAILY_FIELDS2
                                                            .completedAmount
                                                        ] || ""
                                                      }
                                                      onChange={(e) =>
                                                        handleChange(
                                                          DAILY_FIELDS2.completedAmount,
                                                          e.target.value
                                                        )
                                                      }
                                                    />
                                                  </>
                                                ) : (
                                                  `${formatter2.format(
                                                    branchSummary.completed
                                                  )} / ${formatter.format(
                                                    branchSummary.completedAmount.toFixed(
                                                      2
                                                    )
                                                  )}`
                                                )}
                                              </div>
                                              <div className="report-daily-p">
                                                {editingThis ? (
                                                  <>
                                                    <input
                                                      aria-label={`${branch} – ${getFieldLabel(lang, "rejected", AR_FIELD_LABELS)}`}
                                                      className="daily-expand-input"
                                                      type="number"
                                                      value={
                                                        editItem[
                                                          DAILY_FIELDS2.rejected
                                                        ] || ""
                                                      }
                                                      onChange={(e) =>
                                                        handleChange(
                                                          DAILY_FIELDS2.rejected,
                                                          e.target.value
                                                        )
                                                      }
                                                    />
                                                    <input
                                                      aria-label={`${branch} – ${getFieldLabel(lang, "rejectedAmount", AR_FIELD_LABELS)}`}
                                                      className="daily-expand-input"
                                                      type="number"
                                                      value={
                                                        editItem[
                                                          DAILY_FIELDS2
                                                            .rejectedAmount
                                                        ] || ""
                                                      }
                                                      onChange={(e) =>
                                                        handleChange(
                                                          DAILY_FIELDS2.rejectedAmount,
                                                          e.target.value
                                                        )
                                                      }
                                                    />
                                                  </>
                                                ) : (
                                                  `${formatter2.format(
                                                    branchSummary.rejected
                                                  )} / ${formatter.format(
                                                    branchSummary.rejectedAmount
                                                  )}`
                                                )}
                                              </div>
                                              <div className="report-daily-p">
                                                {editingThis ? (
                                                  <>
                                                    <input
                                                      aria-label={`${branch} – ${getFieldLabel(lang, "canceled", AR_FIELD_LABELS)}`}
                                                      className="daily-expand-input"
                                                      type="number"
                                                      value={
                                                        editItem[
                                                          DAILY_FIELDS2.canceled
                                                        ] || ""
                                                      }
                                                      onChange={(e) =>
                                                        handleChange(
                                                          DAILY_FIELDS2.canceled,
                                                          e.target.value
                                                        )
                                                      }
                                                    />
                                                    <input
                                                      aria-label={`${branch} – ${getFieldLabel(lang, "canceledAmount", AR_FIELD_LABELS)}`}
                                                      className="daily-expand-input"
                                                      type="number"
                                                      value={
                                                        editItem[
                                                          DAILY_FIELDS2
                                                            .canceledAmount
                                                        ] || ""
                                                      }
                                                      onChange={(e) =>
                                                        handleChange(
                                                          DAILY_FIELDS2.canceledAmount,
                                                          e.target.value
                                                        )
                                                      }
                                                    />
                                                  </>
                                                ) : (
                                                  `${formatter2.format(
                                                    branchSummary.canceled
                                                  )} / ${formatter.format(
                                                    branchSummary.canceledAmount
                                                  )}`
                                                )}
                                              </div>
                                              <div className="report-daily-p">
                                                {editingThis ? (
                                                  <>
                                                    <input
                                                      aria-label={`${branch} – ${getFieldLabel(lang, "endurance", AR_FIELD_LABELS)}`}
                                                      className="daily-expand-input"
                                                      type="number"
                                                      value={
                                                        editItem[
                                                          DAILY_FIELDS2
                                                            .endurance
                                                        ] || ""
                                                      }
                                                      onChange={(e) =>
                                                        handleChange(
                                                          DAILY_FIELDS2.endurance,
                                                          e.target.value
                                                        )
                                                      }
                                                    />
                                                    <input
                                                      aria-label={`${branch} – ${getFieldLabel(lang, "enduranceAmount", AR_FIELD_LABELS)}`}
                                                      className="daily-expand-input"
                                                      type="number"
                                                      value={
                                                        editItem[
                                                          DAILY_FIELDS2
                                                            .enduranceAmount
                                                        ] || ""
                                                      }
                                                      onChange={(e) =>
                                                        handleChange(
                                                          DAILY_FIELDS2.enduranceAmount,
                                                          e.target.value
                                                        )
                                                      }
                                                    />
                                                  </>
                                                ) : (
                                                  `${formatter2.format(
                                                    branchSummary.endurance
                                                  )} / ${formatter.format(
                                                    branchSummary.enduranceAmount
                                                  )}`
                                                )}
                                              </div>
                                              <div className="report-daily-p">
                                                {editingThis ? (
                                                  <>
                                                    <input
                                                      aria-label={`${branch} – ${getFieldLabel(lang, "refund", AR_FIELD_LABELS)}`}
                                                      className="daily-expand-input"
                                                      type="number"
                                                      value={
                                                        editItem[
                                                          DAILY_FIELDS2.refund
                                                        ] || ""
                                                      }
                                                      onChange={(e) =>
                                                        handleChange(
                                                          DAILY_FIELDS2.refund,
                                                          e.target.value
                                                        )
                                                      }
                                                    />
                                                    <input
                                                      aria-label={`${branch} – ${getFieldLabel(lang, "refundAmount", AR_FIELD_LABELS)}`}
                                                      className="daily-expand-input"
                                                      type="number"
                                                      value={
                                                        editItem[
                                                          DAILY_FIELDS2
                                                            .refundAmount
                                                        ] || ""
                                                      }
                                                      onChange={(e) =>
                                                        handleChange(
                                                          DAILY_FIELDS2.refundAmount,
                                                          e.target.value
                                                        )
                                                      }
                                                    />
                                                  </>
                                                ) : (
                                                  `${formatter2.format(
                                                    branchSummary.refund
                                                  )} / ${formatter.format(
                                                    branchSummary.refundAmount
                                                  )}`
                                                )}
                                              </div>
                                              {has("report.entry.update") && (
                                                <div className="daily-expand-card-body-btns">
                                                  {editingThis ? (
                                                    <>
                                                      <Button
                                                        variant="primary"
                                                        appearance="filled"
                                                        disabled={pending}
                                                        onClick={handleSave}
                                                      >
                                                        {
                                                          translations[lang]
                                                            .financial.save
                                                        }
                                                      </Button>
                                                      <Button
                                                        variant="danger"
                                                        appearance="soft"
                                                        onClick={handleCancel}
                                                      >
                                                        {
                                                          translations[lang]
                                                            .financial.cancel
                                                        }
                                                      </Button>
                                                    </>
                                                  ) : (
                                                    <>
                                                      <Button
                                                        variant="primary"
                                                        appearance="soft"
                                                        rounded
                                                        icon={<Repeat size={20} />}
                                                        aria-label={`${text.edit}: ${branch}`}
                                                        title={text.edit}
                                                        onClick={() =>
                                                          handleEdit(
                                                            branchItems[0]
                                                          )
                                                        }
                                                      >
                                                      </Button>
                                                      <Button
                                                        variant="danger"
                                                        appearance=""
                                                        icon={<Trash2 size={20} />}
                                                        aria-label={`${text.delete}: ${branch}`}
                                                        title={text.delete}
                                                        onClick={() =>
                                                          confirmDelete(
                                                            branchItems[0]._id
                                                          )
                                                        }
                                                      >
                                                      </Button>
                                                    </>
                                                  )}
                                                </div>
                                              )}
                                              {has("report.entry.delete") && (
                                                <div className="daily-expand-card-body-btns">
                                                  <Button
                                                    variant="danger"
                                                    appearance=""
                                                    icon={<Trash2 size={20} />}
                                                    aria-label={`${text.delete}: ${branch}`}
                                                    title={text.delete}
                                                    onClick={() =>
                                                      confirmDelete(
                                                        branchItems[0]._id
                                                      )
                                                    }
                                                  >
                                                  </Button>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        }
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      {deleteTarget && (
        <DeleteModal
          cancel={() => setDeleteTarget(null)}
          id={deleteTarget}
          handleDelete={handleConfirmedDelete}
        />
      )}
    </div>
  );
};
